import { Menu, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { TrialBadge } from '@/components/layout/TrialBadge';
import { UserMenu } from '@/components/layout/UserMenu';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';

/**
 * The header.
 *
 * ## The search box did nothing
 *
 * It was a `<button>` with a magnifying glass, the word "Search…" and a ⌘K badge, and no handler --
 * so it looked exactly like a working search, advertised a shortcut, and sat at the top of every
 * page doing nothing at all. Now it opens {@see CommandPalette}. The Cmd/Ctrl+K shortcut still
 * works for anyone who reaches for it; it is no longer advertised on the control itself.
 *
 * The notifications bell was the same shape of thing and has been removed rather than left
 * decorating the header: there is no notification feed behind it, and a bell that never has
 * anything to show teaches people to ignore a bell.
 */
export function Topbar({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Cmd on a Mac, Ctrl elsewhere. Unadvertised now, so both are accepted rather than
      // expecting anybody to guess which one this build wants.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearching(true);
      }
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header className="glass sticky top-0 z-20 flex h-16 items-center gap-3 border-b px-4 lg:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onToggleSidebar}
          aria-label="Toggle navigation"
        >
          <Menu />
        </Button>

        <button
          type="button"
          onClick={() => setSearching(true)}
          className="hidden h-9 flex-1 items-center gap-2 rounded-md border border-border bg-muted/30 px-3 text-sm text-muted-foreground transition-colors hover:border-primary/40 sm:flex sm:max-w-md"
        >
          <Search className="size-4" />
          <span>Search…</span>
        </button>

        {/* The same thing on a phone, where the full box does not fit. */}
        <Button
          variant="ghost"
          size="icon"
          className="sm:hidden"
          onClick={() => setSearching(true)}
          aria-label="Search"
        >
          <Search />
        </Button>

        <div className="ml-auto flex items-center gap-1">
          <TrialBadge />
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      <CommandPalette open={searching} onClose={() => setSearching(false)} />
    </>
  );
}
